import React, { useState, useEffect, useRef } from 'react';
import { monitoringService } from '../../services/monitoringService';

interface ServiceStatus {
  backend: string;
  listener: string;
  redis: string;
  database: string;
  timestamp: string;
}

interface WorkflowEvent {
  id: string;
  event_type: string;
  entity_id: string;
  processed: boolean;
  created_at: string;
  event_data?: any;
}

interface WorkflowEnrollment {
  id: string;
  workflow_id: string;
  contact_id: string;
  enrollment_status: string;
  enrolled_at: string;
  workflows?: { name: string };
  contacts?: { first_name: string; last_name: string };
}

interface WorkflowExecution {
  id: string;
  workflow_id: string;
  status: string;
  trigger_type: string;
  started_at: string;
  completed_at?: string;
}

export const MonitoringPage: React.FC = () => {
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [logsErrorsOnly, setLogsErrorsOnly] = useState(false);
  const [events, setEvents] = useState<WorkflowEvent[]>([]);
  const [enrollments, setEnrollments] = useState<WorkflowEnrollment[]>([]);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [notificationLogs, setNotificationLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingLogs, setDeletingLogs] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only fetch data on initial page load
    fetchAllData();
  }, []);

  const fetchNotificationLogs = async () => {
    try {
      const data = await monitoringService.getNotificationLogs(50);
      setNotificationLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching notification logs:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStatus(),
        fetchLogs(),
        fetchEvents(),
        fetchEnrollments(),
        fetchExecutions(),
        fetchNotificationLogs(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const data = await monitoringService.getStatus();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const data = await monitoringService.getLogs(100, logsErrorsOnly);
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const data = await monitoringService.getWorkflowEvents(20);
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const data = await monitoringService.getWorkflowEnrollments(20);
      setEnrollments(data.enrollments || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  const fetchExecutions = async () => {
    try {
      const data = await monitoringService.getWorkflowExecutions(20);
      setExecutions(data.executions || []);
    } catch (error) {
      console.error('Error fetching executions:', error);
    }
  };

  const processPendingEvents = async () => {
    if (!confirm('Process all pending workflow events?')) return;
    
    try {
      const result = await monitoringService.processPendingEvents();
      alert(`Processed ${result.processed} events, ${result.failed} failed`);
      fetchEvents();
      fetchEnrollments();
      fetchExecutions();
    } catch (error: any) {
      alert('Error: ' + (error.message || 'Failed to process events'));
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'running' || status === 'connected') {
      return 'text-green-600 bg-green-50';
    }
    if (status === 'stopped' || status === 'disconnected' || status === 'error') {
      return 'text-red-600 bg-red-50';
    }
    return 'text-gray-600 bg-gray-50';
  };

  const getLogLineColor = (line: string) => {
    if (line.includes('ERROR') || line.includes('error')) {
      return 'text-red-400';
    }
    if (line.includes('WARN') || line.includes('warn')) {
      return 'text-yellow-400';
    }
    if (line.includes('INFO') || line.includes('info')) {
      return 'text-blue-400';
    }
    if (line.includes('✅') || line.includes('success')) {
      return 'text-green-400';
    }
    return 'text-gray-300';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Backend Monitoring</h1>
        <p className="mt-1 text-sm text-admin-600">
          Real-time monitoring of backend services, logs, and workflow events
        </p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="admin-card p-4">
          <div className="text-sm text-admin-500 mb-1">Backend Service</div>
          <div className={`text-xl font-semibold ${getStatusColor(status?.backend || 'unknown')} px-2 py-1 rounded inline-block`}>
            {status?.backend || '-'}
          </div>
        </div>
        <div className="admin-card p-4">
          <div className="text-sm text-admin-500 mb-1">Event Listener</div>
          <div className={`text-xl font-semibold ${getStatusColor(status?.listener || 'unknown')} px-2 py-1 rounded inline-block`}>
            {status?.listener || '-'}
          </div>
        </div>
        <div className="admin-card p-4">
          <div className="text-sm text-admin-500 mb-1">Redis</div>
          <div className={`text-xl font-semibold ${getStatusColor(status?.redis || 'unknown')} px-2 py-1 rounded inline-block`}>
            {status?.redis || '-'}
          </div>
        </div>
        <div className="admin-card p-4">
          <div className="text-sm text-admin-500 mb-1">Database</div>
          <div className={`text-xl font-semibold ${getStatusColor(status?.database || 'unknown')} px-2 py-1 rounded inline-block`}>
            {status?.database || '-'}
          </div>
        </div>
      </div>

      {/* Logs Section */}
      <div className="admin-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-admin-900">Backend Logs</h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={logsErrorsOnly}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  setLogsErrorsOnly(checked);
                  try {
                    const data = await monitoringService.getLogs(100, checked);
                    setLogs(data.logs || []);
                  } catch (err) {
                    console.error('Error fetching logs:', err);
                  }
                }}
                className="rounded"
              />
              <span className="text-admin-600">Errors only</span>
            </label>
            <button
              onClick={fetchLogs}
              className="admin-button h-9 px-4 bg-primary-600 text-white hover:bg-primary-700"
            >
              Refresh
            </button>
            <button
              onClick={async () => {
                if (!confirm('Clear all backend logs? This cannot be undone.')) return;
                setDeletingLogs(true);
                try {
                  const result = await monitoringService.deleteLogs();
                  if (result?.success) {
                    setLogs([]);
                    fetchLogs();
                  } else {
                    alert(result?.error || 'Failed to clear logs');
                  }
                } catch (e) {
                  alert('Error: ' + (e instanceof Error ? e.message : 'Failed to clear logs'));
                } finally {
                  setDeletingLogs(false);
                }
              }}
              disabled={deletingLogs}
              className="admin-button h-9 px-4 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deletingLogs ? 'Clearing...' : 'Clear Logs'}
            </button>
          </div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 h-96 overflow-y-auto font-mono text-sm">
          {logs.length === 0 ? (
            <div className="text-gray-500">No logs available. Click Refresh to load logs.</div>
          ) : (
            logs.map((line, index) => (
              <div key={index} className={`mb-1 ${getLogLineColor(line)}`}>
                {line}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Workflow Events */}
      <div className="admin-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-admin-900">Workflow Events</h2>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchEvents}
              className="admin-button h-9 px-4 bg-primary-600 text-white hover:bg-primary-700"
            >
              Refresh
            </button>
            <button
              onClick={processPendingEvents}
              className="admin-button h-9 px-4 bg-red-600 text-white hover:bg-red-700"
            >
              Process Pending
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-200">
            <thead className="bg-admin-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Processed</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-admin-200">
              {events.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-sm text-admin-500">
                    No events found
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-4 py-3 text-sm text-admin-900 font-mono">
                      {event.id.substring(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-900">{event.event_type}</td>
                    <td className="px-4 py-3 text-sm text-admin-900 font-mono">
                      {event.entity_id.substring(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          event.processed
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {event.processed ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-500">
                      {new Date(event.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Enrollments */}
      <div className="admin-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-admin-900">Workflow Enrollments</h2>
          <button
            onClick={fetchEnrollments}
            className="admin-button h-9 px-4 bg-primary-600 text-white hover:bg-primary-700"
          >
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-200">
            <thead className="bg-admin-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Workflow</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Enrolled</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-admin-200">
              {enrollments.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-4 text-center text-sm text-admin-500">
                    No enrollments found
                  </td>
                </tr>
              ) : (
                enrollments.map((enrollment) => (
                  <tr key={enrollment.id}>
                    <td className="px-4 py-3 text-sm text-admin-900">
                      {enrollment.workflows?.name || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-900">
                      {enrollment.contacts
                        ? `${enrollment.contacts.first_name} ${enrollment.contacts.last_name}`
                        : 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {enrollment.enrollment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-500">
                      {new Date(enrollment.enrolled_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Executions */}
      <div className="admin-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-admin-900">Workflow Executions</h2>
          <button
            onClick={fetchExecutions}
            className="admin-button h-9 px-4 bg-primary-600 text-white hover:bg-primary-700"
          >
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-200">
            <thead className="bg-admin-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Workflow ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Trigger</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Started</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Completed</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-admin-200">
              {executions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-sm text-admin-500">
                    No executions found
                  </td>
                </tr>
              ) : (
                executions.map((execution) => (
                  <tr key={execution.id}>
                    <td className="px-4 py-3 text-sm text-admin-900 font-mono">
                      {execution.workflow_id.substring(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          execution.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : execution.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {execution.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-900">{execution.trigger_type}</td>
                    <td className="px-4 py-3 text-sm text-admin-500">
                      {new Date(execution.started_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-admin-500">
                      {execution.completed_at
                        ? new Date(execution.completed_at).toLocaleString()
                        : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notification Logs */}
      <div className="admin-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-admin-900">Notification Logs</h2>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchNotificationLogs}
              className="admin-button h-9 px-4 bg-primary-600 text-white hover:bg-primary-700"
            >
              Refresh
            </button>
            <button
              onClick={async () => {
                if (confirm('Clear all notification logs?')) {
                  await monitoringService.clearNotificationLogs();
                  fetchNotificationLogs();
                }
              }}
              className="admin-button h-9 px-4 bg-red-600 text-white hover:bg-red-700"
            >
              Clear
            </button>
          </div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
          {notificationLogs.length === 0 ? (
            <div className="text-gray-500">No notifications received yet</div>
          ) : (
            notificationLogs.map((log, index) => (
              <div key={index} className="mb-2 text-gray-300">
                <div className="text-blue-400">
                  [{new Date(log.timestamp).toLocaleString()}] {log.channel}
                </div>
                <div className="ml-4 text-xs text-gray-400">
                  {JSON.stringify(log.payload || log.raw, null, 2)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

